// src/components/shared/FilterChips.js
// Barra de chips/segmented-control genérica para filtro client-side.
//
// Props:
//   chips      — array de { key, label, count, color? }
//                key: string única (usar '__all' para o chip "Todos")
//                label: texto exibido
//                count: número (renderizado entre parênteses)
//                color: token opcional ∈ COLOR_KEYS (default 'slate')
//   activeKey  — string da chip ativa
//   onChange   — (newKey) => void
//   className  — wrapper extra classes
//
// Comportamento:
//   - Se chips.length <= 1 → não renderiza nada (sem filtro útil).
//   - Mantém ordem da prop chips (chamador define).

import React from 'react';

const COLOR_TONE = {
  slate:   { inactive: 'bg-white       text-slate-700   border-slate-200   hover:bg-slate-50',
             active:   'bg-slate-900   text-white       border-slate-900' },
  emerald: { inactive: 'bg-emerald-50  text-emerald-700 border-emerald-200 hover:bg-emerald-100',
             active:   'bg-emerald-600 text-white       border-emerald-600' },
  blue:    { inactive: 'bg-blue-50     text-blue-700    border-blue-200    hover:bg-blue-100',
             active:   'bg-blue-600    text-white       border-blue-600' },
  red:     { inactive: 'bg-red-50      text-red-700     border-red-200     hover:bg-red-100',
             active:   'bg-red-600     text-white       border-red-600' },
  orange:  { inactive: 'bg-orange-50   text-orange-700  border-orange-200  hover:bg-orange-100',
             active:   'bg-orange-600  text-white       border-orange-600' },
  amber:   { inactive: 'bg-amber-50    text-amber-700   border-amber-200   hover:bg-amber-100',
             active:   'bg-amber-600   text-white       border-amber-600' },
  purple:  { inactive: 'bg-purple-50   text-purple-700  border-purple-200  hover:bg-purple-100',
             active:   'bg-purple-600  text-white       border-purple-600' },
  indigo:  { inactive: 'bg-indigo-50   text-indigo-700  border-indigo-200  hover:bg-indigo-100',
             active:   'bg-indigo-600  text-white       border-indigo-600' },
  cyan:    { inactive: 'bg-cyan-50     text-cyan-700    border-cyan-200    hover:bg-cyan-100',
             active:   'bg-cyan-600    text-white       border-cyan-600' },
  pink:    { inactive: 'bg-pink-50     text-pink-700    border-pink-200    hover:bg-pink-100',
             active:   'bg-pink-600    text-white       border-pink-600' },
  teal:    { inactive: 'bg-teal-50     text-teal-700    border-teal-200    hover:bg-teal-100',
             active:   'bg-teal-600    text-white       border-teal-600' },
  violet:  { inactive: 'bg-violet-50   text-violet-700  border-violet-200  hover:bg-violet-100',
             active:   'bg-violet-600  text-white       border-violet-600' },
  rose:    { inactive: 'bg-rose-50     text-rose-700    border-rose-200    hover:bg-rose-100',
             active:   'bg-rose-600    text-white       border-rose-600' },
};

export default function FilterChips({ chips = [], activeKey, onChange, className = '' }) {
  if (!Array.isArray(chips) || chips.length <= 1) return null;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {chips.map((chip) => {
        const tone = COLOR_TONE[chip.color] || COLOR_TONE.slate;
        const isActive = chip.key === activeKey;
        const cls = isActive ? tone.active : tone.inactive;
        return (
          <button
            key={chip.key}
            type="button"
            onClick={() => onChange && onChange(chip.key)}
            className={`px-2.5 py-1 text-[11px] font-medium border rounded-full transition-colors ${cls}`}
          >
            {chip.label}
            {chip.count != null && (
              <span className={`ml-1 ${isActive ? 'opacity-90' : 'opacity-70'}`}>
                ({chip.count})
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
